import { resolveToolName } from "@health/types/ui-tools";
import type {
  ChatItem,
  ChatStatusItem,
  ChatTextItem,
  ChatTraceGroupItem,
  ChatWidgetItem,
} from "@health/ui/components/chat/types";
import type { UIMessage } from "ai";

// ---------------------------------------------------------------------------
// Pure projections + predicates.
//
// Everything in this file is pure: takes part data, returns a ChatItem or a
// boolean. No mutable state, no group bookkeeping. The orchestrator in
// `normalize.ts` composes these into the final `ChatMessageGroup[]`.
// ---------------------------------------------------------------------------

/** Minimal AI SDK tool-part shape we operate on. */
export type ToolPart = {
  type: string;
  toolCallId: string;
  state: string;
  input: Record<string, unknown>;
  output?: unknown;
  submitted?: boolean;
};

type AnyPart = UIMessage["parts"][number];

const TOOL_PREFIX = "tool-";
const SIGNAL_INSIGHT_RAW = "signal_insight";
const SIGNAL_RESOLVED = "signal";

/** Resolve a part's tool name, applying legacy aliases. Empty string for non-tool parts. */
export function toolNameOf(part: { type: string }): string {
  if (!part.type.startsWith(TOOL_PREFIX)) return "";
  return String(resolveToolName(part.type.slice(TOOL_PREFIX.length)));
}

/** Raw (pre-alias-resolution) tool name. Used by checks that distinguish
 *  `signal` vs the legacy `signal_insight` alias. */
function rawToolNameOf(part: { type: string }): string {
  return part.type.startsWith(TOOL_PREFIX) ? part.type.slice(TOOL_PREFIX.length) : "";
}

/** Tool names co-occurring in the same assistant message — used by
 *  trace-card suppression rules (e.g. don't show save trace alongside
 *  show_auth/show_payment). Raw names (no alias resolution) to match the
 *  historic behavior. */
export function siblingToolNamesFrom(parts: AnyPart[]): string[] {
  return parts.filter((p) => p.type.startsWith(TOOL_PREFIX)).map((p) => rawToolNameOf(p));
}

// ---------------------------------------------------------------------------
// Signal disambiguation
// ---------------------------------------------------------------------------

/** True when a `signal` part represents AI-generated insight content
 *  (not a status update). Centralized so the three places that need this
 *  rule agree: signal mapping, trace-group pair detection, and
 *  user-boundary determination. */
export function isSignalInsight(part: {
  type: string;
  input?: unknown;
  output?: unknown;
  state?: string;
}): boolean {
  if (rawToolNameOf(part) === SIGNAL_INSIGHT_RAW) return true;
  const input = part.input as Record<string, unknown> | undefined;
  const output = part.output as Record<string, unknown> | undefined;
  if (input?.kind === "insight") return true;
  if (output?.kind === "insight") return true;
  if (
    output &&
    output.kind !== "status" &&
    Object.hasOwn(output, "eligible") &&
    typeof output.insight === "string"
  ) {
    return true;
  }
  return false;
}

/** Extract `{status, context}` from a signal status part (input or output). */
function extractSignalStatus(toolPart: ToolPart): { status: string; context: string } | null {
  const input = toolPart.input as Record<string, unknown> | undefined;
  const output = toolPart.output as Record<string, unknown> | undefined;

  if (
    input?.kind === "status" ||
    (input?.status != null && input?.context != null && input?.kind !== "insight")
  ) {
    return { status: String(input.status), context: String(input.context) };
  }
  if (
    toolPart.state === "output-available" &&
    output &&
    (output.kind === "status" || (output.status != null && output.context != null))
  ) {
    return { status: String(output.status), context: String(output.context) };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per-part projections — each returns 0 or 1 ChatItem, no side effects.
// ---------------------------------------------------------------------------

/** Project a text part. Honors heading dedup via `seenHeadings` (mutated
 *  by the caller — the Set is the orchestrator's). Returns null for empty
 *  text or duplicate headings. */
export function mapTextPart(
  message: Pick<UIMessage, "id" | "role">,
  partIdx: number,
  part: { type: "text"; text?: string },
  seenHeadings: Set<string>,
): ChatTextItem | null {
  const text = part.text ?? "";
  if (!text) return null;

  const heading = text.match(/^#+\s+.+/m)?.[0]?.trim();
  if (heading) {
    if (seenHeadings.has(heading)) return null;
    seenHeadings.add(heading);
  }

  return {
    kind: "text",
    id: `${message.id}-text-${partIdx}`,
    role: message.role as "user" | "assistant",
    text,
  };
}

/** Project a `signal` tool part into either an insight item, a status item,
 *  or null (insight without `eligible: true`, malformed payload, etc.). */
export function mapSignalPart(
  message: Pick<UIMessage, "id">,
  partIdx: number,
  toolPart: ToolPart,
): ChatItem | null {
  const output = toolPart.output as Record<string, unknown> | undefined;

  if (toolPart.state === "output-available" && output && isSignalInsight(toolPart)) {
    if (output.eligible === true && typeof output.insight === "string" && output.insight) {
      return {
        kind: "insight",
        id: `${message.id}-insight-${partIdx}`,
        text: output.insight,
        variant: "standalone",
      };
    }
    return null;
  }

  if (isSignalInsight(toolPart)) return null;

  const status = extractSignalStatus(toolPart);
  if (!status || (toolPart.state !== "output-available" && toolPart.state !== "input-available")) {
    return null;
  }

  return {
    kind: "status",
    id: `${message.id}-status-${partIdx}`,
    toolName: SIGNAL_RESOLVED,
    status: status.status,
    context: status.context,
    variant: "signal",
  };
}

/** When a save_intake_fields output declares `hide_trace: true`, the
 *  trace card and standalone activity status are suppressed (transition
 *  + other side effects still apply). */
export function getSaveHideTrace(toolPart: ToolPart): boolean {
  if (toolPart.state !== "output-available" || !toolPart.output) return false;
  const out = toolPart.output as Record<string, unknown>;
  return out.hide_trace === true;
}

const SAVE_INTAKE_FIELDS = "save_intake_fields";

/** True when `toolName` is the save_intake_fields tool. */
export function isSaveIntakeFields(toolName: string): boolean {
  return toolName === SAVE_INTAKE_FIELDS;
}

/** If the next part is a signal-insight that pairs with `currentToolName`
 *  (save_intake_fields), return the insight part — otherwise null.
 *  Encodes the "trace-group" detection rule. */
export function detectTraceGroupNext(
  parts: AnyPart[],
  currentIdx: number,
  currentToolName: string,
): ToolPart | null {
  if (!isSaveIntakeFields(currentToolName)) return null;
  const nextPart = parts[currentIdx + 1];
  if (!nextPart || !nextPart.type.startsWith(TOOL_PREFIX)) return null;
  if (toolNameOf(nextPart) !== SIGNAL_RESOLVED) return null;
  if (!isSignalInsight(nextPart as unknown as ToolPart)) return null;
  return nextPart as unknown as ToolPart;
}

/** Build a trace-group item from a save_intake_fields part + its paired
 *  signal-insight part. */
export function mapTraceGroupItem(
  message: Pick<UIMessage, "id">,
  partIdx: number,
  toolName: string,
  saveField: ToolPart,
  insight: ToolPart,
  isLive: boolean,
  siblingToolNames: string[],
): ChatTraceGroupItem {
  const children: ChatItem[] = [
    {
      kind: "status",
      id: `${message.id}-trace-status-${partIdx}`,
      toolName,
      status: "active",
      context: "",
      variant: "activity",
      toolCallId: saveField.toolCallId,
      input: saveField.input,
      toolState: saveField.state,
      isLive,
      siblingToolNames,
    },
  ];

  const insightOut = insight.output as { eligible?: boolean; insight?: string } | undefined;
  if (insight.state === "output-available" && insightOut?.eligible && insightOut.insight) {
    children.push({
      kind: "insight",
      id: `${message.id}-trace-insight-${partIdx}`,
      text: insightOut.insight,
      variant: "inference",
    });
  }

  return {
    kind: "trace-group",
    id: `${message.id}-tracegroup-${partIdx}`,
    children,
  };
}

/** Standalone activity-status item for a visible-status server tool. */
export function mapVisibleStatusItem(
  message: Pick<UIMessage, "id">,
  partIdx: number,
  toolName: string,
  toolPart: ToolPart,
  isLive: boolean,
  siblingToolNames: string[],
): ChatStatusItem {
  return {
    kind: "status",
    id: `${message.id}-toolstatus-${partIdx}`,
    toolName,
    status: toolPart.state === "output-available" ? "complete" : "active",
    context: "",
    variant: "activity",
    toolCallId: toolPart.toolCallId,
    input: toolPart.input,
    toolState: toolPart.state,
    isLive,
    siblingToolNames,
  };
}

/** Project a widget tool part to a ChatWidgetItem (or a `loading`
 *  placeholder while input is still streaming). Returns null when the
 *  tool part isn't yet in a renderable state. */
export function mapWidgetItem(
  messageId: string,
  partIdx: number,
  toolName: string,
  toolPart: ToolPart,
): ChatWidgetItem | ChatItem | null {
  const id = `${messageId}-widget-${partIdx}`;
  const base = {
    kind: "widget" as const,
    id,
    toolName,
    toolCallId: toolPart.toolCallId,
    input: toolPart.input,
  };

  if (toolPart.state === "input-streaming") {
    const supportsStreaming = toolName === "show_chips" || toolName === "show_chip_group";
    if (supportsStreaming && toolPart.input && Object.keys(toolPart.input).length > 0) {
      return { ...base, state: "active", output: undefined, streaming: true };
    }
    return { kind: "loading", id, toolName };
  }

  if (toolPart.state === "input-available") {
    return { ...base, state: "active", output: undefined };
  }

  if (toolPart.state === "output-available") {
    const output = toolPart.output as Record<string, unknown> | null | undefined;

    if (output?._requiresInteraction) return { ...base, state: "active", output };
    // Accept both shapes: legacy `{ action: "cancelled" }` (PhotoUpload, etc.)
    // and canonical `{ kind: "dismissed" }` (AuthWidget).
    if (output?.skipped || output?.action === "cancelled" || output?.kind === "dismissed") {
      return { ...base, state: "skipped", output };
    }
    if (toolPart.submitted === false) return { ...base, state: "skipped", output: undefined };
    return { ...base, state: "submitted", output: output ?? undefined };
  }

  return null;
}
